import React, { useState, useEffect, useRef } from 'react';

const Dropdown = ({
  options = [],
  selectedOption = null,
  onChange = () => {},
  placeholder
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [selectedItems, setSelectedItems] = useState([]);
  const dropdownRef = useRef(null);

  // Initialize selected items from selectedOption prop
  useEffect(() => {
    if (selectedOption) {
      if (Array.isArray(selectedOption)) {
        setSelectedItems(selectedOption);
      } else {
        setSelectedItems([selectedOption]);
      }
    } else {
      setSelectedItems([]);
    }
  }, [selectedOption]);

  // Filter options based on search text
  const filteredOptions = options.filter(option => 
    (option.name || option).toLowerCase().includes(searchText.toLowerCase())
  );

  // Handle checkbox toggle - modified for single choice
  const handleToggleOption = (option) => {
    const optionId = option.id || option;
    const optionValue = option.id ? option : { id: option, name: option };
    
    const isSelected = selectedItems.some(item => (item.id || item) === optionId);
    let newSelectedItems;
    
    if (isSelected) {
      newSelectedItems = [];
    } else {
      // For single choice, replace any existing selections
      newSelectedItems = [optionValue];
    }
    
    setSelectedItems(newSelectedItems);
  };

  // Apply changes on OK button click
  const handleApply = () => {
    // For single choice, pass the first item or null
    onChange(selectedItems.length > 0 ? selectedItems[0] : null);
    setIsOpen(false);
  };

  // Cancel changes
  const handleCancel = () => {
    // Reset to initial selection
    if (selectedOption) {
      if (Array.isArray(selectedOption)) {
        setSelectedItems(selectedOption);
      } else {
        setSelectedItems([selectedOption]);
      }
    } else {
      setSelectedItems([]);
    }
    setIsOpen(false);
  };

  // Reset all selections
  const handleReset = () => {
    setSelectedItems([]);
    onChange(null);
    setIsOpen(false);
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Display value in the button
  const displayValue = () => {
    if (selectedItems.length === 0) {
      return placeholder;
    }
    
    return selectedItems[0].name || selectedItems[0];
  };

  return (
    <div className="relative" style={{ width: '240px' }} ref={dropdownRef}>
      {/* Dropdown trigger button */}
      <button
        className="w-[150px] h-[25px] bg-white border rounded px-2 py-1 text-left flex items-center justify-between"
        onClick={() => setIsOpen(!isOpen)}
      >
        <span className="font-gazer">{displayValue()}</span>
        <svg
          width="12"
          height="6"
          viewBox="0 0 12 6"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          className={`transform transition-transform ${isOpen ? 'rotate-180' : ''}`}
        >
          <path d="M1 1L6 5L11 1" stroke="#2B3674" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      </button>

      {/* Dropdown menu */}
      {isOpen && (
        <div className="absolute z-50 w-full mt-1" style={{ width: '240px' }}>
          <div 
            className="flex flex-col" 
            style={{ 
              width: '220px', 
              backgroundColor: '#EAEFF3',
              margin: '0 auto',
              padding: '10px',
              borderRadius: '4px',
              boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.1)'
            }}
          >
            {/* Search box - Modified with SVG on the left */}
            <div className="relative mb-2" style={{ width: '200px', height: '30px' }}>
              <div className="flex items-center w-full h-full border rounded bg-white px-2">
                {/* Search icon moved to the left */}
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M13 14L10.992 11.9899M12.99 8C12.99 10.7614 10.7536 13 7.99498 13C5.23633 13 3 10.7614 3 8C3 5.23858 5.23633 3 7.99498 3C10.7536 3 12.99 5.23858 12.99 8Z" stroke="#2B3674" strokeWidth="1.4" strokeLinecap="round"/>
                </svg>

                {/* Input field next to the search icon */}
                <input
                  type="text"
                  className="w-full h-full border-none pl-2 outline-none"
                  placeholder="Search"
                  style={{ fontSize: '10px', fontWeight: 400 }}
                  value={searchText}
                  onChange={(e) => setSearchText(e.target.value)}
                />
              </div>
            </div>

            {/* Options list */}
            <div className="overflow-y-auto" style={{ maxHeight: '200px' }}>
              {filteredOptions.length > 0 ? (
                filteredOptions.map((option, index) => {
                  const optionId = option.id || option;
                  const optionName = option.name || option;
                  const isSelected = selectedItems.some(item => (item.id || item) === optionId);
                  
                  return (
                    <div 
                      key={optionId || index} 
                      className="flex items-center mb-1 cursor-pointer font-gazer-dropdown"
                      style={{ width: '200px', height: '30px' }}
                      onClick={() => handleToggleOption(option)}
                    >
                      {/* Checkbox */}
                      {isSelected ? (
                        <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <rect width="16" height="16" rx="4" fill="#0832DE"/>
                          <g clipPath="url(#clip0_16146_212804)">
                            <path d="M12 5.5L6.5 11L4 8.5" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
                          </g>
                          <defs>
                            <clipPath id="clip0_16146_212804">
                              <rect width="12" height="12" fill="white" transform="translate(2 2)"/>
                            </clipPath>
                          </defs>
                        </svg>
                      ) : (
                        <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <rect x="0.5" y="0.5" width="15" height="15" rx="3.5" stroke="#0832DE"/>
                        </svg>
                      )}
                      <span className="ml-2">{optionName}</span>
                    </div>
                  );
                })
              ) : (
                <div className="flex items-center justify-center text-gray-500" style={{ height: '30px' }}>
                  No options found
                </div>
              )}
            </div>

            {/* Action buttons */}
            <div className="flex justify-between mt-2" style={{ width: '200px', height: '30px' }}>
              <button 
                className="bg-[#CED5E5] font-gazer border rounded"
                style={{ width: '125px', height: '30px' }}
                onClick={handleCancel}
              >
                Cancel
              </button>
              <button 
                className="bg-[#0832DE]  text-white rounded"
                style={{ width: '70px', height: '30px' }}
                onClick={handleApply}
              >
                Ok
              </button>
            </div>
          </div>

          {/* Reset button */}
          <div 
            className="flex items-center justify-left mx-auto mt-1 bg-white rounded cursor-pointer hover:bg-gray-50"
            style={{ width: '220px', height: '30px' }}
            onClick={handleReset}
          >
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M8 10.668C9.114 10.668 10 9.78064 10 8.66797C10 7.5553 9.114 6.66797 8 6.66797C6.886 6.66797 6 7.5553 6 8.66797C6 9.78064 6.886 10.668 8 10.668Z" fill="#2B3674"/>
              <path d="M13.878 7.45603C13.7209 6.68827 13.4139 5.95902 12.9747 5.31003C12.5431 4.67155 11.9931 4.12165 11.3547 3.69003C10.7056 3.25092 9.97638 2.94396 9.20867 2.7867C8.8054 2.70492 8.39481 2.66471 7.98333 2.6667V1.33203L5.33333 3.33203L7.98333 5.33203V4.00003C8.306 3.9987 8.62867 4.02936 8.94 4.09336C9.53668 4.21564 10.1035 4.45417 10.608 4.79536C11.1056 5.13086 11.5338 5.55911 11.8693 6.0567C12.3902 6.82685 12.668 7.7356 12.6667 8.66536C12.6666 9.28929 12.5419 9.90691 12.3 10.482C12.1824 10.7591 12.0386 11.0243 11.8707 11.274C11.7021 11.5223 11.5106 11.7542 11.2987 11.9667C10.6533 12.6108 9.83407 13.0527 8.94133 13.238C8.32048 13.3634 7.68085 13.3634 7.06 13.238C6.46304 13.1156 5.89603 12.8769 5.39133 12.5354C4.89432 12.2002 4.46653 11.7724 4.13133 11.2754C3.61104 10.5044 3.33314 9.59547 3.33333 8.66536H2C2.00071 9.86116 2.35792 11.0296 3.026 12.0214C3.4579 12.6588 4.00724 13.2081 4.64467 13.64C5.63508 14.3103 6.80407 14.6676 8 14.6654C8.40618 14.6653 8.81133 14.6245 9.20933 14.5434C9.97649 14.385 10.7053 14.0781 11.3547 13.64C11.6735 13.4252 11.9707 13.1798 12.242 12.9074C12.5137 12.6349 12.7592 12.3376 12.9753 12.0194C13.6451 11.0292 14.0021 9.86075 14 8.66536C14 8.25918 13.9591 7.85404 13.878 7.45603Z" fill="#2B3674"/>
            </svg>
            <span className="ml-2 font-gazer-light">Сбросить</span>
          </div>
        </div>
      )}
    </div>
  );
};

export default Dropdown;